<template>
  <input
    ref="inputRef"
    :value="displayValue"
    @input="handleInput"
    type="text"
    inputmode="numeric"
    min="0"
    :maxlength="10"
    :max="maxAmount"
    class="amount-input w-100 mx-auto text-center"
    aria-label="Withdrawal amount"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";

interface Props {
  modelValue: string;
  maxAmount: number;
  autoFocus?: boolean;
}

interface Emits {
  (e: "update:modelValue", value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  autoFocus: false,
});

const emit = defineEmits<Emits>();

const inputRef = ref<HTMLInputElement | null>(null);
const isKeyboardOpen = ref<boolean>(false);

const displayValue = computed(() => {
  const num = Number(props.modelValue.replace(/[^0-9]/g, ""));
  return props.modelValue === "" ? "$0" : currencyFormat(num, 0);
});

const handleInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const cleanValue = target.value.replace(/[^0-9]/g, "");
  emit("update:modelValue", cleanValue);
};

const focusInput = () => inputRef.value?.focus();

const handleResize = () => {
  isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
};

watch(isKeyboardOpen, (newValue) => {
  if (!newValue) {
    safeAndroidCall("hideNav");
  }
});

onMounted(() => {
  if (props.autoFocus) {
    focusInput();
  }
  window.addEventListener("resize", handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

defineExpose({
  focusInput,
});
</script>

<style lang="scss" scoped>
.amount-input {
  color: var(--Typography-Text-2-ICON, #595a5a);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 40px;
  font-style: normal;
  font-weight: 400;
  line-height: 29px;
  border: none;
  text-align: center;
  outline: none;
}
</style>
